import { useState } from 'react';
import { Link2 } from 'lucide-react';

export interface AttachCandidate {
  id: string;
  title: string;
}

const searchable = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

/**
 * « Rattacher à… » (L48, R4): a person attaches this object (a duplicate, a
 * name written otherwise) to another object of the same list. Everything on it
 * passes there and it disappears; never done automatically.
 */
export const AttachRecordControl = ({ title, candidates, onAttach }: {
  title: string;
  candidates: AttachCandidate[];
  onAttach: (intoRecordId: string) => Promise<void>;
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<AttachCandidate | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const shown = candidates.filter((candidate) => searchable(candidate.title).includes(searchable(query.trim())));

  if (!open) {
    return (
      <button type="button" role="menuitem" onClick={() => setOpen(true)}
        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-domino-ink hover:bg-domino-muted">
        <Link2 className="h-4 w-4 text-domino-inkSoft" />Rattacher à…
      </button>
    );
  }

  const confirm = async () => {
    if (!chosen) return;
    setPending(true);
    setError(null);
    try {
      await onAttach(chosen.id);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Le rattachement n’a pas pu être fait.');
      setPending(false);
    }
  };

  return (
    <div className="space-y-2 px-2 py-1.5 text-sm">
      {chosen ? (
        <>
          <p className="text-domino-ink">Rattacher « {title} » à <b>{chosen.title}</b> ?</p>
          <div className="flex gap-2">
            <button type="button" disabled={pending} onClick={() => void confirm()}
              className="h-8 rounded-lg bg-domino-aubergine px-3 text-xs font-semibold text-white active:scale-[0.97] disabled:opacity-60">
              {pending ? 'Rattachement…' : 'Rattacher'}
            </button>
            <button type="button" disabled={pending} onClick={() => setChosen(null)} className="h-8 px-2 text-xs text-domino-inkSoft hover:text-domino-ink">Retour</button>
          </div>
        </>
      ) : (
        <>
          <label className="block text-xs text-domino-inkSoft" htmlFor="attach-record-query">Rattacher à</label>
          <input id="attach-record-query" autoFocus value={query} onChange={(event) => setQuery(event.target.value)}
            className="h-8 w-full rounded-lg border border-domino-cardBorder px-2 text-sm focus:outline-none focus:ring-2 focus:ring-domino-prune" />
          <ul className="max-h-48 overflow-y-auto">
            {shown.map((candidate) => (
              <li key={candidate.id}>
                <button type="button" onClick={() => setChosen(candidate)}
                  className="w-full rounded-lg px-2 py-1.5 text-left text-sm text-domino-ink hover:bg-domino-muted">{candidate.title}</button>
              </li>
            ))}
            {shown.length === 0 && <li className="px-2 py-1.5 text-sm text-domino-inkSoft">Aucune ligne de ce nom.</li>}
          </ul>
          <button type="button" onClick={() => setOpen(false)} className="text-xs text-domino-inkSoft hover:text-domino-ink">Fermer</button>
        </>
      )}
      {error && <p role="alert" className="text-xs text-domino-inkSoft">{error}</p>}
    </div>
  );
};
